import React, { useEffect, useState } from 'react';
import { Building2, Save, Globe, Phone, Mail, MapPin, Hash, AlertTriangle, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api';
import { PhoneInput } from '../../components/PhoneInput';

const PAYS_OPTIONS = [
  'France', 'Belgique', 'Suisse', 'Luxembourg', 'Canada', 'Maroc', 'Algérie', 'Tunisie',
  'Sénégal', "Côte d'Ivoire", 'Cameroun',
];

const inputCls = `w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-200`;
const inputStyle = { borderColor: 'var(--border-light)', background: 'white', color: 'var(--text-primary)' };
const LABEL_W = 'w-32 flex-shrink-0 text-xs font-semibold uppercase tracking-wide text-right';

function Row({ label, icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3">
      <span className={LABEL_W} style={{ color: 'var(--text-muted)' }}>{label}</span>
      <div className="flex-1 relative">
        {Icon && <Icon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />}
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>{title}</p>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="border-t" style={{ borderColor: 'var(--border-light)' }} />;
}

export default function Organisation() {
  const { user: currentUser } = useAuth();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [annuaireLocked, setAnnuaireLocked] = useState(false);
  const [lockToggling, setLockToggling] = useState(false);

  const isSuperAdmin = currentUser?.role === 'super_admin';

  // Transfer ownership state
  const [showTransfer, setShowTransfer] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [transferUserId, setTransferUserId] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    api.getCurrentOrg()
      .then(r => { setOrg(r.data); setForm(r.data); })
      .catch(() => toast.error("Impossible de charger l'organisation"))
      .finally(() => setLoading(false));
    api.getAnnuaireLock()
      .then(r => setAnnuaireLocked(r.data.annuaire_locked))
      .catch(() => {});
  }, []);

  const handleToggleLock = async () => {
    setLockToggling(true);
    try {
      const r = await api.setAnnuaireLock(!annuaireLocked);
      setAnnuaireLocked(r.data.annuaire_locked);
      toast.success(r.data.annuaire_locked ? 'Annuaire verrouillé — migration Pennylane active' : 'Annuaire déverrouillé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLockToggling(false);
    }
  };

  const isOwner = org?.owner_id === currentUser?.user_id;

  const openTransfer = async () => {
    try {
      const r = await api.getUsers();
      setOrgUsers((r.data || []).filter(u => u.user_id !== currentUser?.user_id && u.actif));
    } catch {
      toast.error('Impossible de charger les utilisateurs');
      return;
    }
    setTransferUserId('');
    setConfirmName('');
    setShowTransfer(true);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.nom?.trim()) return toast.error('Le nom est requis');
    setSaving(true);
    try {
      const updated = await api.updateCurrentOrg({
        nom: form.nom,
        siret: form.siret || null,
        adresse: form.adresse || null,
        code_postal: form.code_postal || null,
        ville: form.ville || null,
        pays: form.pays || null,
        email: form.email || null,
        telephone: form.telephone || null,
      });
      setOrg(updated.data);
      setForm(updated.data);
      toast.success('Organisation mise à jour');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferUserId || confirmName !== org?.nom) return;
    setTransferring(true);
    try {
      const r = await api.transferOrgOwnership(transferUserId);
      toast.success(`Propriété transférée à ${r.data.new_owner}`);
      setOrg(prev => ({ ...prev, owner_id: transferUserId }));
      setShowTransfer(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du transfert');
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" style={{ color: 'var(--text-muted)' }}>
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-transparent"
          style={{ borderColor: 'var(--primary-solid)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-xl" style={{ background: 'var(--primary-50)' }}>
            <Building2 size={20} style={{ color: 'var(--primary-solid)' }} />
          </div>
          <h1 className="text-xl font-bold" style={{  color: 'var(--text-primary)' }}>
            Organisation
          </h1>
        </div>
        <p className="text-sm ml-11" style={{ color: 'var(--text-muted)' }}>
          Informations générales de votre organisation
        </p>
      </div>

      {/* Main card */}
      <div className="rounded-2xl border p-6 space-y-5" style={{ borderColor: 'var(--border-light)', background: 'white' }}>

        <Section title="Identité">
          <Row label="Nom *">
            <input
              data-testid="org-nom-input"
              className={inputCls}
              style={inputStyle}
              value={form.nom || ''}
              onChange={e => set('nom', e.target.value)}
              placeholder="Ex: MBud SAS"
            />
          </Row>
          <Row label="SIRET" icon={Hash}>
            <input
              data-testid="org-siret-input"
              className={`${inputCls} pl-8`}
              style={inputStyle}
              value={form.siret || ''}
              onChange={e => set('siret', e.target.value)}
              placeholder="123 456 789 00012"
            />
          </Row>
          <Row label="Abonnement">
            <input
              className={inputCls}
              style={{ ...inputStyle, background: 'var(--surface-1)', color: 'var(--text-muted)', cursor: 'not-allowed' }}
              value={org?.plan_abonnement || ''}
              readOnly
            />
          </Row>
        </Section>

        <Divider />

        <Section title="Contact">
          <Row label="Email" icon={Mail}>
            <input
              data-testid="org-email-input"
              className={`${inputCls} pl-8`}
              style={inputStyle}
              type="email"
              value={form.email || ''}
              onChange={e => set('email', e.target.value)}
              placeholder="contact@example.com"
            />
          </Row>
          <Row label="Téléphone" icon={Phone}>
            <PhoneInput
              data-testid="org-tel-input"
              className={`${inputCls} pl-8`}
              style={inputStyle}
              value={form.telephone || ''}
              onChange={e => set('telephone', e.target.value)}
            />
          </Row>
        </Section>

        <Divider />

        <Section title="Adresse">
          <Row label="Rue" icon={MapPin}>
            <input
              data-testid="org-adresse-input"
              className={`${inputCls} pl-8`}
              style={inputStyle}
              value={form.adresse || ''}
              onChange={e => set('adresse', e.target.value)}
              placeholder="12 rue des Acacias"
            />
          </Row>
          <Row label="Code postal">
            <input
              data-testid="org-cp-input"
              className={inputCls}
              style={inputStyle}
              value={form.code_postal || ''}
              onChange={e => set('code_postal', e.target.value)}
              placeholder="75001"
            />
          </Row>
          <Row label="Ville">
            <input
              data-testid="org-ville-input"
              className={inputCls}
              style={inputStyle}
              value={form.ville || ''}
              onChange={e => set('ville', e.target.value)}
              placeholder="Paris"
            />
          </Row>
          <Row label="Pays" icon={Globe}>
            <select
              data-testid="org-pays-select"
              className={`${inputCls} pl-8 appearance-none`}
              style={inputStyle}
              value={form.pays || ''}
              onChange={e => set('pays', e.target.value)}
            >
              <option value="">— Sélectionner —</option>
              {PAYS_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </Row>
        </Section>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          data-testid="org-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: 'var(--primary-gradient)' }}
        >
          <Save size={15} />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      {/* Verrouillage annuaire — migration Pennylane (super_admin uniquement) */}
      {isSuperAdmin && (
        <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: annuaireLocked ? '#FCA5A5' : 'var(--border-light)', background: 'white' }}>
          <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ background: annuaireLocked ? '#FFF5F5' : 'var(--surface-1)', borderColor: annuaireLocked ? '#FCA5A5' : 'var(--border-light)' }}>
            <ShieldAlert size={18} style={{ color: annuaireLocked ? '#DC2626' : 'var(--text-muted)' }} />
            <span className="text-sm font-bold uppercase tracking-wide" style={{ color: annuaireLocked ? '#DC2626' : 'var(--text-muted)' }}>
              Verrouillage annuaire — Migration Pennylane
            </span>
            {annuaireLocked && (
              <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#FEE2E2', color: '#DC2626' }}>ACTIF</span>
            )}
          </div>
          <div className="px-6 py-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {annuaireLocked ? 'Annuaire verrouillé' : 'Annuaire déverrouillé'}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {annuaireLocked
                  ? 'Modifications, suppressions et fusions des entreprises sont bloquées pour tous les utilisateurs (y compris super_admin). Les créations restent possibles.'
                  : 'Activez ce verrou pendant la migration Pennylane pour figer les noms et garantir la cohérence des rapprochements de tiers.'}
              </p>
            </div>
            <button
              data-testid="toggle-annuaire-lock-btn"
              onClick={handleToggleLock}
              disabled={lockToggling}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-colors"
              style={{
                borderColor: annuaireLocked ? '#FCA5A5' : '#D1D5DB',
                color: annuaireLocked ? '#DC2626' : 'var(--text-muted)',
                background: annuaireLocked ? '#FEF2F2' : 'transparent',
                opacity: lockToggling ? 0.6 : 1,
              }}>
              {annuaireLocked ? 'Déverrouiller' : 'Verrouiller'}
            </button>
          </div>
        </div>
      )}

      {/* Zone de danger — visible uniquement pour le propriétaire */}
      {isOwner && (
        <div className="rounded-2xl border-2 border-red-200 overflow-hidden" style={{ background: 'white' }}>
          <div className="flex items-center gap-3 px-6 py-4 border-b border-red-100" style={{ background: '#FFF5F5' }}>
            <ShieldAlert size={18} className="text-red-500" />
            <span className="text-sm font-bold text-red-700 uppercase tracking-wide">Zone de danger</span>
          </div>
          <div className="px-6 py-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Transférer la propriété
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                Cédez la propriété de <strong>{org?.nom}</strong> à un autre membre. Cette action est irréversible.
              </p>
            </div>
            <button
              data-testid="transfer-ownership-btn"
              onClick={openTransfer}
              className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold border-2 border-red-300 text-red-600 hover:bg-red-50 transition-colors"
            >
              Transférer
            </button>
          </div>
        </div>
      )}

      {/* Modal de transfert */}
      {showTransfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="rounded-2xl border-2 border-red-200 p-6 w-full max-w-md space-y-5"
            style={{ background: 'white' }}>

            {/* Header */}
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-red-50">
                <AlertTriangle size={20} className="text-red-500" />
              </div>
              <div>
                <h2 className="font-bold text-base" style={{  color: 'var(--text-primary)' }}>
                  Transférer la propriété
                </h2>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Action irréversible</p>
              </div>
            </div>

            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Vous allez transférer la propriété de <strong>{org?.nom}</strong> à un autre membre.
              Vous resterez membre de l'organisation mais ne serez plus propriétaire.
            </p>

            {/* Nouveau propriétaire */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                Nouveau propriétaire
              </label>
              <select
                data-testid="transfer-user-select"
                value={transferUserId}
                onChange={e => setTransferUserId(e.target.value)}
                className={`${inputCls}`}
                style={inputStyle}
              >
                <option value="">— Sélectionner un membre —</option>
                {orgUsers.map(u => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Confirmation par nom */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                Tapez <span className="text-red-600 font-mono normal-case">{org?.nom}</span> pour confirmer
              </label>
              <input
                data-testid="transfer-confirm-input"
                className={inputCls}
                style={{ ...inputStyle, borderColor: confirmName && confirmName !== org?.nom ? '#FCA5A5' : 'var(--border-light)' }}
                value={confirmName}
                onChange={e => setConfirmName(e.target.value)}
                placeholder={org?.nom}
                autoComplete="off"
              />
              {confirmName && confirmName !== org?.nom && (
                <p className="text-xs text-red-500">Le nom ne correspond pas</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button
                data-testid="transfer-confirm-btn"
                onClick={handleTransfer}
                disabled={!transferUserId || confirmName !== org?.nom || transferring}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-40 transition-colors"
              >
                {transferring ? 'Transfert...' : 'Confirmer le transfert'}
              </button>
              <button
                onClick={() => setShowTransfer(false)}
                className="px-4 py-2.5 rounded-xl text-sm font-medium"
                style={{ color: 'var(--text-muted)', border: '1px solid var(--border-light)' }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
